<template>
  <div class="template-triggers-container">
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <div class="mt-4 text-gray-500 font-medium">Fetching template triggers...</div>
    </div>

    <div
      v-else-if="!triggers.length"
      class="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300 mx-4"
    >
      <v-icon size="80" color="grey lighten-2">mdi-lightning-bolt-outline</v-icon>
      <h3 class="mt-4 text-xl font-semibold text-gray-700">No Triggers Defined</h3>
      <p class="text-gray-500 mt-2 max-w-sm mx-auto">
        Define triggers to automatically send templates based on system events.
      </p>
      <v-btn color="primary" @click="openTriggerDialog" outlined class="mt-4 normal-case px-6">
        Create Your First Trigger
      </v-btn>
    </div>

    <v-card v-else flat class="mx-4 border-light pa-2">
      <v-data-table
        :headers="tableHeaders"
        :items="triggers"
        :loading="loading"
        class="custom-triggers-table"
        hide-default-footer
        disable-pagination
      >
        <!-- Trigger Name & Event -->
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <div class="bg-amber-50 p-2 rounded-lg mr-3">
              <v-icon color="amber darken-3" size="18">mdi-lightning-bolt</v-icon>
            </div>
            <div>
              <div class="text-sm font-bold text-gray-800">{{ item.name }}</div>
              <div class="text-[10px] text-amber-600 font-bold tracking-tight">
                {{ formatEventName(item.event) }}
              </div>
            </div>
          </div>
        </template>

        <!-- Module -->
        <template v-slot:item.module="{ item }">
          <v-chip x-small label class="text-blue-grey darken-3 font-bold">
            {{ formatModuleName(item.module) }}
          </v-chip>
        </template>

        <!-- Channel -->
        <template v-slot:item.channel="{ item }">
          <v-chip
            x-small
            label
            :color="getTypeStyle(item.channel || item.letterTemplateId?.type).bg"
            :class="`${
              getTypeStyle(item.channel || item.letterTemplateId?.type).color
            }--text font-bold`"
          >
            <v-icon
              left
              x-small
              :color="getTypeStyle(item.channel || item.letterTemplateId?.type).color"
            >
              {{ getTypeStyle(item.channel || item.letterTemplateId?.type).icon }}
            </v-icon>
            {{ item.channel || item.letterTemplateId?.type || 'EMAIL' }}
          </v-chip>
        </template>

        <!-- Linked Template -->
        <template v-slot:item.template="{ item }">
          <div class="d-flex align-center py-2">
            <div class="bg-blue-50 p-1.5 rounded mr-3 border border-blue-100">
              <v-icon small color="blue">mdi-file-document-outline</v-icon>
            </div>
            <div class="d-flex flex-column">
              <span class="text-sm font-bold text-blue-900 leading-tight">{{
                item.letterTemplateId?.name || 'Unknown'
              }}</span>
              <v-chip
                x-small
                label
                :color="getTypeStyle(item.letterTemplateId?.type).bg"
                :class="`${getTypeStyle(item.letterTemplateId?.type).color}--text font-bold mt-1`"
                class="elevation-0"
                style="height: 18px"
              >
                <v-icon left x-small :color="getTypeStyle(item.letterTemplateId?.type).color">{{
                  getTypeStyle(item.letterTemplateId?.type).icon
                }}</v-icon>
                {{ item.letterTemplateId?.type || 'No Type' }}
              </v-chip>
            </div>
          </div>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  @click="editTrigger(item)"
                  class="mr-2"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit Trigger</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="error"
                  @click="confirmDelete(item)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>Delete Trigger</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Trigger Dialog -->
    <v-dialog v-model="triggerDialog" persistent width="1000px">
      <v-card flat class="d-flex flex-column" style="height: 85vh">
        <v-card-title
          class="py-4 px-6 d-flex justify-space-between border-b border-gray-200 bg-white"
        >
          <h3 class="text-xl font-bold text-gray-800 ml-2">
            {{ isEditing ? 'Edit Template Trigger' : 'Create New Trigger' }}
          </h3>
          <v-btn icon @click="triggerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <div class="overflow-y-auto flex-grow-1 p-6 bg-gray-50">
          <v-row>
            <!-- Top Form Section -->
            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-6 bg-white border-gray-200 shadow-sm">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.name"
                      label="Trigger Name*"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"
                      placeholder="e.g. Disbursal Welcome Message"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="form.module"
                      :items="usecases"
                      label="Use Case / Module*"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"
                      @change="onModuleChange"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="form.event"
                      :items="filteredTriggerEvents"
                      label="System Event*"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"
                      item-text="text"
                      item-value="value"
                      :disabled="!form.module"
                      placeholder="Select a use case first"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="form.channel"
                      :items="['EMAIL', 'WHATSAPP', 'SMS']"
                      label="Channel*"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"
                      @change="onChannelChange"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="form.letterTemplateId"
                      :items="filteredTemplates"
                      label="Select Template*"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"
                      item-text="name"
                      item-value="_id"
                      :disabled="!form.event || !form.channel"
                      placeholder="Select an event and channel first"
                    >
                      <template v-slot:item="{ item }">
                        <div class="d-flex align-center py-1">
                          <v-icon small color="blue" class="mr-2">mdi-file-document-outline</v-icon>
                          <div class="d-flex flex-column">
                            <span class="font-medium text-sm">{{ item.name }}</span>
                            <span class="text-[10px] text-gray-400">{{ item.type }}</span>
                          </div>
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                <div v-if="selectedTemplatePreview" class="mt-4 pt-4 border-t border-gray-100">
                  <v-row dense align="center">
                    <v-col cols="auto">
                      <div class="text-[10px] text-gray-400 uppercase font-bold">
                        Template Details:
                      </div>
                    </v-col>
                    <v-col cols="auto">
                      <v-chip
                        x-small
                        label
                        :color="getTypeStyle(selectedTemplatePreview.type).bg"
                        :class="`${
                          getTypeStyle(selectedTemplatePreview.type).color
                        }--text font-bold px-2`"
                      >
                        <v-icon
                          left
                          x-small
                          :color="getTypeStyle(selectedTemplatePreview.type).color"
                        >
                          {{ getTypeStyle(selectedTemplatePreview.type).icon }}
                        </v-icon>
                        {{ selectedTemplatePreview.type }}
                      </v-chip>
                    </v-col>
                    <v-col cols="auto" v-if="selectedTemplatePreview.module">
                      <v-chip x-small label color="grey lighten-4">{{
                        selectedTemplatePreview.module
                      }}</v-chip>
                    </v-col>
                    <v-col
                      cols="auto"
                      v-if="selectedTemplatePreview.attachments?.length > 0"
                      class="ml-4"
                    >
                      <div class="text-[10px] text-gray-400 uppercase font-bold mr-2 inline-block">
                        Attachments:
                      </div>
                      <v-chip
                        v-for="att in selectedTemplatePreview.attachments"
                        :key="att._id"
                        x-small
                        color="blue-grey lighten-5"
                        class="text-blue-grey text-[10px] mr-1"
                      >
                        <v-icon x-small left>mdi-paperclip</v-icon>
                        {{ att.name || 'Attachment' }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </div>
              </v-card>
            </v-col>

            <!-- Bottom Preview Section -->
            <v-col cols="12">
              <v-card
                outlined
                class="rounded-lg bg-white border-gray-200 d-flex flex-column overflow-hidden shadow-sm"
                style="min-height: 400px"
              >
                <div class="pa-4 border-b border-gray-100 bg-gray-50 d-flex align-center">
                  <v-icon small color="primary" class="mr-2">mdi-eye-outline</v-icon>
                  <span class="text-sm font-bold text-gray-700">Template Preview</span>
                </div>
                <div class="pa-6 overflow-y-auto bg-white" style="max-height: 600px">
                  <div v-if="selectedTemplatePreview">
                    <div
                      v-if="selectedTemplatePreview.subject"
                      class="mb-4 pa-3 bg-blue-50/30 rounded border border-blue-100/50"
                    >
                      <div class="text-[10px] text-blue-600 font-bold uppercase mb-1">Subject</div>
                      <div class="text-sm font-medium">{{ selectedTemplatePreview.subject }}</div>
                    </div>
                    <div
                      class="preview-content-area text-sm"
                      v-html="selectedTemplatePreview.content"
                    ></div>
                  </div>
                  <div
                    v-else
                    class="d-flex flex-column align-center justify-center py-12 text-gray-300"
                  >
                    <v-icon size="48" color="grey lighten-3">mdi-file-eye-outline</v-icon>
                    <div class="text-xs mt-2">Select a template to see preview</div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <v-card-actions class="pa-6 border-t border-gray-200 bg-white">
          <v-spacer></v-spacer>
          <v-btn text @click="triggerDialog = false" class="normal-case px-6">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            @click="saveTrigger"
            :loading="saving"
            class="normal-case rounded-lg px-8 font-bold shadow-sm"
          >
            {{ isEditing ? 'Update Trigger' : 'Save Trigger' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="bg-red-50 text-red-800 py-4 px-6 font-bold">
          Delete Trigger?
        </v-card-title>
        <v-card-text class="pa-6 text-gray-600">
          Are you sure you want to delete the
          <span class="font-bold text-gray-800">"{{ selectedTrigger?.name }}"</span>? This action
          cannot be undone and may affect your automated communications.
        </v-card-text>
        <v-card-actions class="pa-4 bg-gray-50">
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" class="normal-case">Cancel</v-btn>
          <v-btn
            color="error"
            depressed
            @click="deleteTrigger"
            class="normal-case rounded-lg px-6 font-bold shadow-sm"
          >
            Confirm Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'TemplateTriggers',
  data() {
    return {
      triggers: [],
      templates: [],
      usecases: [],
      allTriggerEvents: [],
      loading: false,
      saving: false,
      triggerDialog: false,
      deleteDialog: false,
      isEditing: false,
      selectedTrigger: null,
      form: {
        name: '',
        module: '',
        event: '',
        channel: 'EMAIL',
        letterTemplateId: ''
      }
    }
  },
  computed: {
    selectedTemplatePreview() {
      if (!this.form.letterTemplateId) return null
      return this.templates.find((t) => t._id === this.form.letterTemplateId)
    },
    filteredTemplates() {
      let filtered = this.templates
      if (this.form.module) {
        filtered = filtered.filter((t) => t.module === this.form.module)
      }
      if (this.form.channel) {
        filtered = filtered.filter((t) => t.type === this.form.channel)
      }
      // Exclude ATTACHMENT type templates in Triggers
      return filtered.filter((t) => t.type !== 'ATTACHMENT')
    },
    filteredTriggerEvents() {
      if (!this.form.module) return []
      return this.allTriggerEvents.filter((e) => e.module === this.form.module)
    },
    tableHeaders() {
      return [
        { text: 'Trigger Name', value: 'name', sortable: false },
        { text: 'Module', value: 'module', sortable: false },
        { text: 'Channel', value: 'channel', sortable: false },
        { text: 'Linked Template', value: 'template', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  async mounted() {
    await Promise.all([this.fetchTriggers(), this.fetchTemplates(), this.fetchTriggerEvents()])
  },
  methods: {
    async fetchTriggers() {
      try {
        this.loading = true
        const response = await this.$axios.get('/template-triggers')
        this.triggers = response.data?.data || response.data || []
      } catch (error) {
        console.error('Error fetching triggers:', error)
      } finally {
        this.loading = false
      }
    },
    async fetchTemplates() {
      try {
        const response = await this.$axios.get('/letter-templates', { params: { limit: '*' } })
        this.templates = response.data?.data || response.data || []
      } catch (error) {
        console.error('Error fetching templates:', error)
      }
    },
    async fetchTriggerEvents() {
      try {
        const response = await this.$axios.get('/template-params-mapping')
        const rawData = response.data?.data || response.data || {}

        const modulesList = []
        const events = []
        Object.entries(rawData).forEach(([moduleKey, moduleData]) => {
          if (moduleKey === 'global' || moduleKey === 'other') return

          if (moduleData.triggers && Array.isArray(moduleData.triggers)) {
            modulesList.push({
              text: moduleKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              value: moduleKey
            })
            moduleData.triggers.forEach((trigger) => {
              events.push({
                text: this.formatEventName(trigger),
                value: trigger,
                module: moduleKey
              })
            })
          }
        })
        this.usecases = modulesList
        this.allTriggerEvents = events
      } catch (error) {
        console.error('Error fetching trigger events:', error)
      }
    },
    onModuleChange() {
      this.form.event = ''
      this.form.letterTemplateId = ''
    },
    onChannelChange() {
      this.form.letterTemplateId = ''
    },
    openTriggerDialog() {
      this.isEditing = false
      this.form = {
        name: '',
        module: '',
        event: '',
        channel: 'EMAIL',
        letterTemplateId: ''
      }
      this.triggerDialog = true
    },
    editTrigger(trigger) {
      this.isEditing = true
      this.selectedTrigger = trigger
      this.form = {
        name: trigger.name,
        module: trigger.module || '',
        event: trigger.event || '',
        channel: trigger.channel || 'EMAIL',
        letterTemplateId: trigger.letterTemplateId?._id || trigger.letterTemplateId || ''
      }
      this.triggerDialog = true
    },
    async saveTrigger() {
      if (
        !this.form.name ||
        !this.form.module ||
        !this.form.event ||
        !this.form.channel ||
        !this.form.letterTemplateId
      ) {
        this.$toast.error('Please fill all required fields')
        return
      }

      try {
        this.saving = true
        if (this.isEditing) {
          await this.$axios.patch(`/template-triggers/${this.selectedTrigger._id}`, this.form)
        } else {
          await this.$axios.post('/template-triggers', this.form)
        }
        this.$toast.success('Trigger saved successfully')
        this.triggerDialog = false
        await this.fetchTriggers()
      } catch (error) {
        this.$toast.error('Failed to save trigger')
      } finally {
        this.saving = false
      }
    },
    confirmDelete(trigger) {
      this.selectedTrigger = trigger
      this.deleteDialog = true
    },
    async deleteTrigger() {
      try {
        await this.$axios.delete(`/template-triggers/${this.selectedTrigger._id}`)
        this.$toast.success('Trigger deleted')
        this.deleteDialog = false
        await this.fetchTriggers()
      } catch (error) {
        this.$toast.error('Failed to delete trigger')
      }
    },
    formatModuleName(name) {
      if (!name) return '-'
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    },
    formatEventName(name) {
      if (!name) return '-'
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    },
    getTypeStyle(type) {
      switch (type) {
        case 'SMS':
          return { bg: 'amber lighten-5', color: 'amber darken-4', icon: 'mdi-message-processing' }
        case 'WHATSAPP':
          return { bg: 'green lighten-5', color: 'green darken-4', icon: 'mdi-whatsapp' }
        case 'EMAIL':
          return { bg: 'blue lighten-5', color: 'blue darken-4', icon: 'mdi-email-outline' }
        case 'ATTACHMENT':
          return { bg: 'purple lighten-5', color: 'purple darken-4', icon: 'mdi-paperclip' }
        default:
          return { bg: 'grey lighten-4', color: 'grey darken-3', icon: 'mdi-file-outline' }
      }
    }
  }
}
</script>

<style scoped>
.template-triggers-container {
  padding: 24px;
  background-color: #f9fafb;
  min-height: 80vh;
}

.custom-triggers-table >>> th {
  background-color: #f5f5f5 !important;
  color: #333 !important;
  font-weight: bold !important;
}

.border-light {
  border: 1px solid #e0e0e0 !important;
}

.preview-content-area {
  background: white;
  min-height: 200px;
}

.preview-content-area :deep(table) {
  width: 100%;
  border-collapse: collapse;
}

.preview-content-area :deep(td),
.preview-content-area :deep(th) {
  border: 1px solid #e5e7eb;
  padding: 8px;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 10px;
}
</style>
